/* ============================================================================
 * pqvData.js — PQ Vision (PQV) data + storage helpers.
 *
 * Backed by the existing `pqv_walkthrough`, `pqv_area`, `pqv_photo`, and
 * `pqv_note` tables (see sql/011_pqv_vision_schema.sql). The schema is final;
 * this module is read/write only — no DDL.
 *
 * Critical: every INSERT into a pqv_* table MUST set `contractor_id` to the
 * authenticated user's id (auth.users.id). RLS depends on this. We mirror the
 * caching pattern from pqiTracker.js so callers can stay declarative.
 *
 * Storage:
 *   Bucket id: `pqv-photos` (private). Path: `<auth.uid()>/<walkthrough_id>/<filename>`.
 *   Photos are uploaded with the contractor's path prefix so Storage RLS can
 *   scope reads/writes by user. We return BOTH the storage_path (canonical,
 *   stored in pqv_photo.storage_path) AND a short-lived signed URL for display.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

export const PQV_PHOTOS_BUCKET = 'pqv-photos';
export const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour

/* ----------------------------------------------------------------------------
 * Contractor id resolver (auth.users.id). Cached for the session; cleared on
 * sign-out. Identical pattern to pqiTracker.getContractorId.
 * ------------------------------------------------------------------------- */
let cachedContractorId = null;

export const getContractorId = async () => {
  if (cachedContractorId) return cachedContractorId;
  const { data } = await supabase.auth.getUser();
  cachedContractorId = data?.user?.id || null;
  return cachedContractorId;
};

export const _resetPqvContractorCache = () => {
  cachedContractorId = null;
};

const requireContractor = async () => {
  const id = await getContractorId();
  if (!id) throw new Error('Not authenticated.');
  return id;
};

/* ----------------------------------------------------------------------------
 * Walkthroughs
 *
 * Note (schema update): pqv_walkthrough now FK's to estimates.id via
 * estimate_id. The legacy job_id column has been removed from the schema;
 * walkthroughs are part 1 of an estimate, not of a job.
 * ------------------------------------------------------------------------- */
export const startWalkthrough = async (estimateId) => {
  if (!estimateId) throw new Error('estimateId is required to start a walkthrough.');
  await requireContractor(); // ensure a session; tenant is stamped server-side
  const payload = {
    // contractor_id is stamped to the caller's tenant by the _stamp_tenant
    // BEFORE INSERT trigger (migration 041) — do NOT set it to auth.uid().
    estimate_id: estimateId,
    status: 'in_progress',
    started_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('pqv_walkthrough')
    .insert(payload)
    .select('id,contractor_id,estimate_id,status,started_at')
    .single();
  if (error) throw error;
  return data;
};

export const completeWalkthrough = async (walkthroughId) => {
  if (!walkthroughId) throw new Error('walkthroughId is required.');
  const { data, error } = await supabase
    .from('pqv_walkthrough')
    .update({ status: 'complete' })
    .eq('id', walkthroughId)
    .select('id,contractor_id,estimate_id,status,started_at')
    .single();
  if (error) throw error;
  return data;
};

export const fetchWalkthrough = async (walkthroughId) => {
  const { data, error } = await supabase
    .from('pqv_walkthrough')
    .select('id,contractor_id,estimate_id,status,started_at')
    .eq('id', walkthroughId)
    .single();
  if (error) throw error;
  return data;
};

/**
 * List walkthroughs for the current TENANT. RLS scopes this to
 * contractor_id = _my_tenant() (migration 041), so no explicit contractor
 * filter is needed — and an auth.uid() filter would be wrong for non-owner
 * team members whose uid != the tenant id.
 */
export const listWalkthroughs = async () => {
  const { data, error } = await supabase
    .from('pqv_walkthrough')
    .select('id,contractor_id,estimate_id,status,started_at')
    .order('started_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Look up the most recent walkthrough for a given estimate. Used by the
 * Estimates page to decide whether to show "PQ Vision GO" (no walkthrough
 * yet) vs "PQ RE-View" (open the existing one). Returns null when none
 * exist. RLS scopes by contractor_id automatically.
 */
export const fetchLatestWalkthroughForEstimate = async (estimateId) => {
  if (!estimateId) return null;
  const { data, error } = await supabase
    .from('pqv_walkthrough')
    .select('id,contractor_id,estimate_id,status,started_at')
    .eq('estimate_id', estimateId)
    .order('started_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data || null;
};

/* ----------------------------------------------------------------------------
 * Findings — PQ Vision AI analysis (Phase 1)
 *
 * analyzeWalkthrough() calls the `pqv-analyze` Edge Function, which runs the
 * vision model server-side (key never on the client) and writes proposed
 * findings. The UI then reads them and the contractor confirms/edits/rejects —
 * each decision is a labeled learning signal (pqv_profile_signal).
 * ------------------------------------------------------------------------- */
export const analyzeWalkthrough = async (walkthroughId) => {
  if (!walkthroughId) throw new Error('walkthroughId is required.');
  const { data, error } = await supabase.functions.invoke('pqv-analyze', {
    body: { walkthroughId },
  });
  if (error) throw error;
  if (data && data.ok === false) {
    throw new Error(data.error || 'Analysis failed.');
  }
  // Background mode: the function answers { pending: true } immediately and
  // analyzes detached (big walkthroughs used to blow the request wall clock).
  // Callers poll fetchWalkthroughStatus until it leaves 'analyzing'.
  return data; // { ok, walkthroughId, pending: true }
};

export const fetchWalkthroughStatus = async (walkthroughId) => {
  if (!walkthroughId) return null;
  const { data, error } = await supabase
    .from('pqv_walkthrough')
    .select('id,status')
    .eq('id', walkthroughId)
    .maybeSingle();
  if (error) throw error;
  return data?.status || null;
};

const FINDING_COLS =
  'id,walkthrough_id,area_id,photo_id,type,source,status,description,evidence,confidence,quantity,created_at';

export const listFindings = async (walkthroughId) => {
  if (!walkthroughId) return [];
  const query = (cols) =>
    supabase
      .from('pqv_finding')
      .select(cols)
      .eq('walkthrough_id', walkthroughId)
      .order('created_at', { ascending: true });
  // resolution/resolved_at arrive with migration 088 (a finding the
  // contractor ANSWERED). Until it's applied, fall back to the old columns.
  let { data, error } = await query(`${FINDING_COLS},resolution,resolved_at`);
  if (error && (error.code === '42703' || /does not exist|could not find the/i.test(error.message || ''))) {
    ({ data, error } = await query(FINDING_COLS));
  }
  if (error) throw error;
  return data || [];
};

/**
 * Apply a review decision to a finding: set its status and (optionally) edit
 * its fields, then log the labeled learning signal. Signal write is best-effort
 * — a telemetry hiccup must never block the contractor's review.
 *
 * @param finding  the finding row (needs id, type; area_name optional)
 * @param status   'confirmed' | 'rejected' | 'edited'
 * @param edits    optional { description, quantity } when editing
 */
export const reviewFinding = async (finding, status, edits = null) => {
  if (!finding?.id) throw new Error('finding with id is required.');
  const patch = { status };
  if (edits && typeof edits === 'object') {
    if (typeof edits.description === 'string') patch.description = edits.description;
    if ('quantity' in edits) patch.quantity = edits.quantity;
  }
  const { data, error } = await supabase
    .from('pqv_finding')
    .update(patch)
    .eq('id', finding.id)
    .select(FINDING_COLS)
    .single();
  if (error) throw error;

  // Learning loop: every confirm/reject/edit is a labeled signal.
  try {
    await supabase.from('pqv_profile_signal').insert({
      finding_id: finding.id,
      area_name: finding.area_name || null,
      finding_type: finding.type,
      signal: status, // 'confirmed' | 'rejected' | 'edited'
    });
  } catch {
    /* non-blocking */
  }
  return data;
};
export const listAreas = async (walkthroughId) => {
  if (!walkthroughId) return [];
  const { data, error } = await supabase
    .from('pqv_area')
    .select('id,contractor_id,walkthrough_id,name,sequence')
    .eq('walkthrough_id', walkthroughId)
    .order('sequence', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const addArea = async ({ walkthroughId, name, sequence }) => {
  if (!walkthroughId) throw new Error('walkthroughId is required.');
  await requireContractor(); // session required; tenant stamped server-side
  const payload = {
    walkthrough_id: walkthroughId,
    name: (name || '').trim() || 'Untitled area',
    sequence: Number.isFinite(sequence) ? sequence : 0,
  };
  const { data, error } = await supabase
    .from('pqv_area')
    .insert(payload)
    .select('id,contractor_id,walkthrough_id,name,sequence')
    .single();
  if (error) throw error;
  return data;
};

/* ----------------------------------------------------------------------------
 * Photos — Storage upload + pqv_photo row
 * ------------------------------------------------------------------------- */
const safeExtFromFile = (file) => {
  const fromMime = (file?.type || '').split('/')[1] || '';
  if (/^[a-z0-9]+$/i.test(fromMime)) {
    return fromMime.toLowerCase() === 'jpeg' ? 'jpg' : fromMime.toLowerCase();
  }
  const fromName = (file?.name || '').split('.').pop() || '';
  if (/^[a-z0-9]{1,5}$/i.test(fromName)) return fromName.toLowerCase();
  return 'jpg';
};

const randSuffix = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().split('-')[0];
  }
  return Math.random().toString(36).slice(2, 10);
};

/**
 * Upload one file to Storage and insert the corresponding pqv_photo row.
 * Path convention: `<auth.uid()>/<walkthrough_id>/<timestamp>-<rand>.<ext>`.
 */
export const uploadAreaPhoto = async ({
  file,
  areaId,
  walkthroughId,
  sequence,
}) => {
  if (!file) throw new Error('file is required.');
  if (!areaId) throw new Error('areaId is required.');
  if (!walkthroughId) throw new Error('walkthroughId is required.');
  // The storage path is still prefixed by the UPLOADER's uid — that's what
  // the pqv-photos storage RLS (016) scopes on. The pqv_photo DB row, by
  // contrast, is tenant-stamped by the _stamp_tenant trigger (041).
  const uid = await requireContractor();

  const ext = safeExtFromFile(file);
  const ts = Date.now();
  const filename = `${ts}-${randSuffix()}.${ext}`;
  const storage_path = `${uid}/${walkthroughId}/${filename}`;

  const { error: upErr } = await supabase.storage
    .from(PQV_PHOTOS_BUCKET)
    .upload(storage_path, file, {
      cacheControl: '3600',
      contentType: file.type || 'image/jpeg',
      upsert: false,
    });
  if (upErr) throw upErr;

  const payload = {
    // contractor_id stamped to tenant by trigger (041) — not set here.
    area_id: areaId,
    storage_path,
    captured_at: new Date().toISOString(),
    // sequence is a Postgres int4 (max ~2.1B) — a raw Date.now() overflows it
    // ("value ... is out of range for type integer", the chat-attach bug).
    // Fallback = seconds since 2020: monotonic, int-safe until ~2088.
    sequence: Number.isFinite(sequence) && sequence < 2000000000
      ? sequence
      : Math.floor((ts - 1577836800000) / 1000),
  };
  const { data, error } = await supabase
    .from('pqv_photo')
    .insert(payload)
    .select('id,contractor_id,area_id,storage_path,captured_at,sequence')
    .single();
  if (error) {
    // Best-effort cleanup if the row insert fails — leaves no orphaned blob.
    try {
      await supabase.storage.from(PQV_PHOTOS_BUCKET).remove([storage_path]);
    } catch (_) {
      /* swallow */
    }
    throw error;
  }
  return data;
};

export const listAreaPhotos = async (areaId) => {
  if (!areaId) return [];
  const { data, error } = await supabase
    .from('pqv_photo')
    .select('id,contractor_id,area_id,storage_path,captured_at,sequence')
    .eq('area_id', areaId)
    .order('sequence', { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Bulk-fetch photos for many areas in one round trip. Returns a map keyed by
 * area_id → array of photo rows. Used by the read-only Vision viewer.
 */
export const listPhotosForAreas = async (areaIds = []) => {
  if (!areaIds.length) return {};
  const { data, error } = await supabase
    .from('pqv_photo')
    .select('id,contractor_id,area_id,storage_path,captured_at,sequence')
    .in('area_id', areaIds)
    .order('sequence', { ascending: true });
  if (error) throw error;
  const out = {};
  for (const id of areaIds) out[id] = [];
  for (const row of data || []) {
    if (!out[row.area_id]) out[row.area_id] = [];
    out[row.area_id].push(row);
  }
  return out;
};

/**
 * Create a short-lived signed URL for a single storage_path so the browser
 * can render the photo without making the bucket public. The TTL is short on
 * purpose — refresh on each page load.
 */
export const signedUrlForPath = async (storagePath) => {
  if (!storagePath) return null;
  const { data, error } = await supabase.storage
    .from(PQV_PHOTOS_BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
  if (error) {
    console.warn('[pqv] signed url failed:', error.message || error);
    return null;
  }
  return data?.signedUrl || null;
};

/**
 * Batched variant — one round trip for many storage_paths. Returns a map of
 * path → signedUrl. Paths whose signing fails are mapped to null so callers
 * can render a placeholder instead of crashing.
 */
export const signedUrlsForPaths = async (storagePaths = []) => {
  const paths = (storagePaths || []).filter(Boolean);
  if (!paths.length) return {};
  const { data, error } = await supabase.storage
    .from(PQV_PHOTOS_BUCKET)
    .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS);
  if (error) {
    console.warn('[pqv] signed urls failed:', error.message || error);
    return Object.fromEntries(paths.map((p) => [p, null]));
  }
  const out = {};
  for (const row of data || []) {
    out[row.path] = row.signedUrl || null;
  }
  return out;
};

/* ----------------------------------------------------------------------------
 * Notes
 * ------------------------------------------------------------------------- */
export const addAreaNote = async ({
  areaId,
  walkthroughId,
  body,
  source = 'typed',
}) => {
  if (!areaId) throw new Error('areaId is required.');
  if (!walkthroughId) throw new Error('walkthroughId is required.');
  if (!body || !body.trim()) throw new Error('Note body is required.');
  await requireContractor(); // session required; tenant stamped server-side
  const payload = {
    area_id: areaId,
    walkthrough_id: walkthroughId,
    body: body.trim(),
    source,
  };
  const { data, error } = await supabase
    .from('pqv_note')
    .insert(payload)
    .select('id,contractor_id,area_id,walkthrough_id,body,source')
    .single();
  if (error) throw error;
  return data;
};

export const listAreaNotes = async (areaId) => {
  if (!areaId) return [];
  const { data, error } = await supabase
    .from('pqv_note')
    .select('id,contractor_id,area_id,walkthrough_id,body,source')
    .eq('area_id', areaId)
    .order('id', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const listNotesForAreas = async (areaIds = []) => {
  if (!areaIds.length) return {};
  const { data, error } = await supabase
    .from('pqv_note')
    .select('id,contractor_id,area_id,walkthrough_id,body,source')
    .in('area_id', areaIds)
    .order('id', { ascending: true });
  if (error) throw error;
  const out = {};
  for (const id of areaIds) out[id] = [];
  for (const row of data || []) {
    if (!out[row.area_id]) out[row.area_id] = [];
    out[row.area_id].push(row);
  }
  return out;
};

/**
 * Update an existing note's body. Used by the autosave debounce on the
 * capture page — once a note row exists, subsequent edits PATCH that row in
 * place rather than inserting a new one.
 */
export const updateAreaNoteBody = async (noteId, body) => {
  if (!noteId) throw new Error('noteId is required.');
  const { data, error } = await supabase
    .from('pqv_note')
    .update({ body: (body || '').trim() })
    .eq('id', noteId)
    .select('id,contractor_id,area_id,walkthrough_id,body,source')
    .single();
  if (error) throw error;
  return data;
};

/* ----------------------------------------------------------------------------
 * Deletes — used by the SHIT button on the capture page.
 *
 * Note (storage): when deleting a photo we MUST also remove the file from
 * the pqv-photos bucket so we don't leak orphaned blobs. The DB-row delete
 * runs first; only if it succeeds do we attempt the storage remove. A
 * storage failure is logged but not rethrown — the canonical record (the
 * row) is already gone, and Supabase storage is eventually consistent.
 * ------------------------------------------------------------------------- */
export const deleteAreaNote = async (noteId) => {
  if (!noteId) throw new Error('noteId is required.');
  const { error } = await supabase.from('pqv_note').delete().eq('id', noteId);
  if (error) throw error;
};

export const deleteAreaPhoto = async (photo) => {
  if (!photo?.id) throw new Error('photo with id is required.');
  const { error } = await supabase
    .from('pqv_photo')
    .delete()
    .eq('id', photo.id);
  if (error) throw error;
  if (photo.storage_path) {
    try {
      await supabase.storage
        .from(PQV_PHOTOS_BUCKET)
        .remove([photo.storage_path]);
    } catch (e) {
      console.warn('[pqv] storage cleanup failed (row already gone):', e);
    }
  }
};

/**
 * Delete an area. Per spec the SHIT button cascades the area's content as
 * well — we wipe its photos (rows + storage objects) and notes first so the
 * UI doesn't leave orphaned children visible if the schema's ON DELETE
 * behavior differs from cascade.
 */
export const deleteArea = async (areaId) => {
  if (!areaId) throw new Error('areaId is required.');

  // Fetch child photos so we can clean up storage too.
  const { data: photos, error: pErr } = await supabase
    .from('pqv_photo')
    .select('id,storage_path')
    .eq('area_id', areaId);
  if (pErr) throw pErr;

  // Notes first (no storage side effects).
  const { error: nErr } = await supabase
    .from('pqv_note')
    .delete()
    .eq('area_id', areaId);
  if (nErr) throw nErr;

  // Then photo rows.
  const { error: phErr } = await supabase
    .from('pqv_photo')
    .delete()
    .eq('area_id', areaId);
  if (phErr) throw phErr;

  // Then the area itself.
  const { error: aErr } = await supabase
    .from('pqv_area')
    .delete()
    .eq('id', areaId);
  if (aErr) throw aErr;

  // Storage cleanup last (best-effort).
  const paths = (photos || [])
    .map((p) => p.storage_path)
    .filter(Boolean);
  if (paths.length) {
    try {
      await supabase.storage.from(PQV_PHOTOS_BUCKET).remove(paths);
    } catch (e) {
      console.warn('[pqv] storage cleanup failed:', e);
    }
  }
};

/* ----------------------------------------------------------------------------
 * Draft estimate scope from CONFIRMED findings — the arrow that closes the
 * Vision loop: walkthrough → findings → contractor review → estimate.
 *
 * Rules (the product's trust doctrine):
 *   - Only findings the contractor CONFIRMED or EDITED are used. Proposed and
 *     rejected findings never touch the estimate.
 *   - One estimate section per area, named after the area. Scope-type findings
 *     become "Scope of work" bullets; flagged quantities are carried along as
 *     "~value unit (verify on site)" — referenced, never asserted.
 *   - Complications become a warning callout ("Site conditions to note").
 *   - NO pricing is generated. Sections arrive with empty line items — the
 *     contractor prices. AI drafts scope; the human owns the numbers.
 *   - Idempotent-ish: areas whose name already exists as a section on the
 *     estimate are skipped, so re-drafting can't duplicate sections.
 * ------------------------------------------------------------------------- */
export const draftScopeFromFindings = async (walkthroughId) => {
  if (!walkthroughId) throw new Error('walkthroughId is required.');
  // Lazy import avoids paying estimatesData's weight on every pqv screen.
  const { fetchEstimate, saveEstimate, normalizeSection } = await import(
    '@/lib/estimatesData'
  );

  const walkthrough = await fetchWalkthrough(walkthroughId);
  if (!walkthrough?.estimate_id) {
    throw new Error('This walkthrough is not linked to an estimate.');
  }

  const [areas, findings] = await Promise.all([
    listAreas(walkthroughId),
    listFindings(walkthroughId),
  ]);
  const usable = findings.filter(
    (f) => f.status === 'confirmed' || f.status === 'edited'
  );
  if (usable.length === 0) {
    throw new Error('No confirmed findings yet — review the analysis first.');
  }

  const estimate = await fetchEstimate(walkthrough.estimate_id);
  const existingNames = new Set(
    (estimate.sections || []).map((s) => String(s.name || '').trim().toLowerCase())
  );

  const quantitySuffix = (q) => {
    if (!q || typeof q !== 'object' || q.value === undefined || q.value === null)
      return '';
    const unit = q.unit ? ` ${q.unit}` : '';
    return ` — ~${q.value}${unit} (verify on site)`;
  };

  // Safety-net dedupe: re-analysis generations can leave near-verbatim
  // duplicate confirmed findings; collapse exact (normalized-text) repeats
  // per area so they can't reach the proposal twice.
  const normText = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const newSections = [];
  let skippedExisting = 0;
  for (const area of areas) {
    const seen = new Set();
    const mine = usable.filter((f) => {
      if (f.area_id !== area.id) return false;
      const key = normText(f.description);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (mine.length === 0) continue;
    const name = String(area.name || 'Area').trim();
    if (existingNames.has(name.toLowerCase())) {
      skippedExisting += 1;
      continue;
    }
    const scopeItems = mine
      .filter((f) => f.type !== 'complication')
      .map((f) => `${f.description}${quantitySuffix(f.quantity)}`);
    const complications = mine
      .filter((f) => f.type === 'complication')
      .map((f) => f.description);

    const blocks = [];
    if (scopeItems.length > 0) {
      blocks.push({ type: 'bullets', title: 'Scope of work', items: scopeItems });
    }
    if (complications.length > 0) {
      blocks.push({
        type: 'callout',
        tone: 'warning',
        title: 'Site conditions to note',
        // One line per condition — the callout renders whitespace-pre-wrap,
        // so newlines beat the unreadable " • " run-on for long lists.
        text: complications.map((c) => `• ${c}`).join('\n'),
      });
    }
    // Plain-text mirror so the scope is visible even in itemized layout —
    // complications included, prefixed so they read as flags, not scope.
    const notesLines = [
      ...scopeItems.map((s) => `• ${s}`),
      ...complications.map((c) => `⚠ Site condition: ${c}`),
    ];
    newSections.push(
      normalizeSection({
        name,
        kind: 'base',
        blocks,
        items: [], // pricing stays the contractor's — AI never fills a number
        notes: notesLines.join('\n'),
      })
    );
  }

  if (newSections.length === 0) {
    return {
      estimateId: estimate.id,
      estimateTitle: estimate.title || estimate.project_name || 'Estimate',
      added: 0,
      skippedExisting,
      confirmedUsed: usable.length,
    };
  }

  // The drafted blocks only render in the open-doc Scope layout. Flip an
  // itemized estimate over when its existing sections are all effectively
  // EMPTY (placeholder sections with no priced/described items and no blocks
  // — e.g. the default blank "Scope" section) — that's still a fresh
  // estimate. An estimate with real authored content keeps the contractor's
  // chosen layout; the plain-text notes mirror covers visibility there.
  const sectionIsEmpty = (s) =>
    (!Array.isArray(s?.blocks) || s.blocks.length === 0) &&
    (!Array.isArray(s?.items) ||
      s.items.every((it) => !String(it?.description || '').trim() && !Number(it?.amount))) &&
    !s?.bottom_line_mode &&
    !String(s?.notes || '').trim();
  const effectivelyFresh = (estimate.sections || []).every(sectionIsEmpty);
  const next = {
    ...estimate,
    sections: [...(estimate.sections || []), ...newSections],
    layout: effectivelyFresh && estimate.layout !== 'tm' ? 'scope' : estimate.layout,
  };
  const saved = await saveEstimate(next);
  return {
    estimateId: saved.id,
    estimateTitle: saved.title || saved.project_name || 'Estimate',
    added: newSections.length,
    skippedExisting,
    confirmedUsed: usable.length,
  };
};
